import { type Extensions } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import StarterKit, { type StarterKitOptions } from '@tiptap/starter-kit';

type PresentationTextBlockHeadingLevels = NonNullable<Exclude<StarterKitOptions['heading'], false>['levels']>;

// biome-ignore lint/style/noMagicNumbers: the contract's heading levels ARE the named constant — h1-h3.
const PRESENTATION_TEXT_BLOCK_HEADING_LEVELS: PresentationTextBlockHeadingLevels = [1, 2, 3];

/**
 * The presentation text block document schema contract, shared by every surface that touches a
 * text block's ProseMirror body — the public viewer's static renderer today, the collection-link
 * editor's live editor next. Both must import THIS array so the schema can never drift between
 * what the editor writes and what the viewer can parse.
 *
 * The contract is: headings 1-3, paragraph, bold, italic, bullet/ordered lists, hard break,
 * http(s)-only links, and left/center/right/justify alignment on headings and paragraphs.
 * Everything else StarterKit bundles that would widen the document schema (blockquote, code,
 * code block, horizontal rule, strike, underline) is disabled, so authored docs can never carry
 * node or mark types the presentation surfaces don't render. StarterKit's schema-neutral editor
 * behaviors (undo/redo, drop/gap cursor, list keymap, trailing node) stay enabled — they add no
 * document types, and the editor wants them.
 */
export const PRESENTATION_TEXT_BLOCK_EXTENSIONS: Extensions = [
  StarterKit.configure({
    blockquote: false,
    code: false,
    codeBlock: false,
    heading: { levels: PRESENTATION_TEXT_BLOCK_HEADING_LEVELS },
    horizontalRule: false,
    // Replaced by the standalone Link below so its protocol allowlist is part of the contract.
    link: false,
    strike: false,
    underline: false,
  }),
  Link.configure({
    // The public viewer renders links statically and the editor must not navigate mid-edit.
    openOnClick: false,
    protocols: ['http', 'https'],
    // Only absolute http(s) URLs may become links — mailto:, javascript:, relative paths, and
    // bare hosts all fail, on top of linkify's own default validation.
    isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && /^https?:\/\//i.test(url),
    HTMLAttributes: {
      rel: 'noopener noreferrer nofollow',
      target: '_blank',
    },
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
];
